import {
  ArrowLeft,
  Copy,
  Eye,
  Loader2,
  MailCheck,
  MousePointerClick,
  OctagonX,
  TriangleAlert,
  UserMinus,
  Users,
} from "lucide-react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useAgents } from "../useAgents";
import { DeleteCampaignButton } from "./DeleteCampaignButton";
import {
  EMAIL_PATH,
  SEGMENTS,
  STATUS_CLASS,
  STATUS_LABEL,
  cancelCampaign,
  formatDateTime,
  percent,
  renderEmail,
  type EmailCampaign,
  type EmailSend,
} from "./email";

type View = "all" | "clicked" | "opened" | "unopened" | "problems";
const VIEWS: { key: View; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "clicked", label: "Hicieron clic" },
  { key: "opened", label: "Abrieron" },
  { key: "unopened", label: "No abrieron" },
  { key: "problems", label: "Fallos y bajas" },
];

const SEND_STATUS: Record<EmailSend["status"], string> = {
  queued: "En cola",
  sending: "Enviando",
  sent: "Enviado",
  failed: "Falló",
  cancelled: "Cancelado",
  skipped: "No enviado (baja)",
};

const Stat = ({
  icon: Icon,
  label,
  value,
  detail,
  accent,
}: {
  icon: typeof Users;
  label: string;
  value: number | string;
  detail?: string;
  accent?: boolean;
}) => (
  <div
    className={cn(
      "flex flex-col gap-1 rounded-2xl border p-4",
      accent &&
        "border-lime-400 bg-lime-100 text-neutral-900 dark:bg-lime-400/15 dark:text-lime-100",
    )}
  >
    <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
    <span className="text-3xl font-bold leading-none">{value}</span>
    {detail ? (
      <span className="text-xs text-muted-foreground">{detail}</span>
    ) : null}
  </div>
);

/** Numbers and people of a campaign that has been sent (or is going out). */
export const CampaignResults = ({
  campaign,
  onChanged,
}: {
  campaign: EmailCampaign;
  onChanged: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const { meId, nameOf } = useAgents();
  const [view, setView] = useState<View>("all");
  const [showEmail, setShowEmail] = useState(false);
  const [stopping, setStopping] = useState(false);
  const live = ["sending", "scheduled"].includes(campaign.status);
  const { data: sends = [], refetch: refetchSends } = useGetList<EmailSend>(
    "gula_email_sends",
    {
      filter: { campaign_id: campaign.id },
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "id", order: "ASC" },
    },
    { refetchInterval: live ? 10_000 : 60_000 },
  );
  // The people list follows the campaign numbers (sent, opens, clicks)
  useEffect(() => {
    refetchSends();
  }, [
    campaign.status,
    campaign.sent,
    campaign.opened,
    campaign.clicked,
    campaign.unsubscribed,
    refetchSends,
  ]);

  const total = campaign.total ?? sends.length;
  const sent = campaign.sent ?? 0;
  const opened = campaign.opened ?? 0;
  const clicked = campaign.clicked ?? 0;
  const rows = sends.filter((send) => {
    switch (view) {
      case "clicked":
        return !!send.clicked_at;
      case "opened":
        return !!send.opened_at;
      case "unopened":
        return send.status === "sent" && !send.opened_at;
      case "problems":
        return (
          ["failed", "skipped"].includes(send.status) || !!send.unsubscribed_at
        );
      default:
        return send.status !== "cancelled";
    }
  });
  const sortedRows =
    view === "all"
      ? [...rows].sort(
          (a, b) =>
            (b.click_count ? 2 : b.opened_at ? 1 : 0) -
            (a.click_count ? 2 : a.opened_at ? 1 : 0),
        )
      : rows;

  const html = useMemo(
    () => (showEmail ? renderEmail(campaign, { firstName: "María" }).html : ""),
    [campaign, showEmail],
  );

  const duplicate = async () => {
    try {
      const { data } = await dataProvider.create("gula_email_campaigns", {
        data: {
          name: `${campaign.name} (copia)`,
          segment: campaign.segment,
          subject: campaign.subject,
          preheader: campaign.preheader ?? "",
          style: campaign.style,
          blocks: campaign.blocks,
          template_id: campaign.template_id,
          created_by: meId ?? null,
        },
      });
      navigate(`${EMAIL_PATH}/${data.id}`);
    } catch (error) {
      console.error(error);
      notify("No se ha podido duplicar", { type: "error" });
    }
  };

  const stop = async () => {
    setStopping(true);
    try {
      await cancelCampaign(campaign.id);
      notify("Envío parado: lo que faltaba ya no sale", { type: "info" });
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="icon" className="rounded-full">
          <Link to={EMAIL_PATH} title="Volver a Correo">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-semibold">{campaign.name}</h1>
          <p className="truncate text-sm text-muted-foreground">
            «{campaign.subject}» ·{" "}
            {SEGMENTS.find((item) => item.value === campaign.segment)?.label}
            {campaign.sent_by
              ? ` · enviada por ${nameOf(campaign.sent_by)}`
              : ""}
            {campaign.started_at
              ? ` · ${formatDateTime(campaign.started_at)}`
              : campaign.scheduled_at
                ? ` · programada para el ${formatDateTime(campaign.scheduled_at)}`
                : ""}
          </p>
        </div>
        <span
          className={cn(
            "rounded-full px-3 py-1 text-sm font-medium",
            STATUS_CLASS[campaign.status],
          )}
        >
          {STATUS_LABEL[campaign.status]}
        </span>
        <Button variant="outline" onClick={() => setShowEmail(!showEmail)}>
          <Eye className="h-4 w-4" />
          {showEmail ? "Ocultar correo" : "Ver correo"}
        </Button>
        <Button variant="outline" onClick={duplicate}>
          <Copy className="h-4 w-4" />
          Duplicar
        </Button>
        <DeleteCampaignButton
          campaign={campaign}
          onDeleted={() => navigate(EMAIL_PATH)}
        />
      </div>

      {live ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-amber-100 p-4 text-amber-950 dark:bg-amber-400/10 dark:text-amber-100">
          <Loader2 className="h-5 w-5 animate-spin" />
          <div className="flex-1 text-sm">
            {campaign.status === "scheduled" ? (
              <>
                Saldrá el{" "}
                <strong>{formatDateTime(campaign.scheduled_at)}</strong> a{" "}
                {total} personas.
              </>
            ) : (
              <>
                Enviando: <strong>{sent}</strong> de {total}. Sale poco a poco
                para no superar el límite de IONOS; puedes cerrar esta página.
              </>
            )}
            {campaign.last_error ? (
              <span className="mt-1 flex items-center gap-1.5">
                <TriangleAlert className="h-4 w-4" />
                {campaign.last_error}
              </span>
            ) : null}
          </div>
          <Button variant="outline" onClick={stop} disabled={stopping}>
            <OctagonX className="h-4 w-4" />
            Parar envío
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat icon={Users} label="Destinatarios" value={total} />
        <Stat
          icon={MailCheck}
          label="Enviados"
          value={sent}
          detail={
            campaign.failed
              ? `${campaign.failed} fallidos`
              : live
                ? `${campaign.pending ?? 0} en cola`
                : undefined
          }
        />
        <Stat
          icon={Eye}
          label="Abiertos"
          value={opened}
          detail={`${percent(opened, sent)} de los enviados`}
        />
        <Stat
          icon={MousePointerClick}
          label="Clics"
          value={clicked}
          detail={`${percent(clicked, sent)} · llámales`}
          accent={clicked > 0}
        />
        <Stat
          icon={UserMinus}
          label="Bajas"
          value={campaign.unsubscribed ?? 0}
          detail={percent(campaign.unsubscribed, sent)}
        />
      </div>

      {showEmail ? (
        <div className="flex justify-center overflow-hidden rounded-xl border bg-[#f4f4f0]">
          <iframe
            title="Correo enviado"
            srcDoc={html}
            sandbox=""
            className="h-[70vh] w-full max-w-3xl bg-white"
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {VIEWS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setView(item.key)}
              className={cn(
                "rounded-full border px-3 py-1 text-sm",
                view === item.key
                  ? "border-foreground bg-foreground text-background"
                  : "hover:bg-muted",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Persona</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Abierto</th>
                <th className="px-3 py-2 font-medium">Clics</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedRows.map((send) => (
                <tr
                  key={send.id}
                  className={cn(
                    send.click_count && "bg-lime-50 dark:bg-lime-400/10",
                  )}
                >
                  <td className="px-3 py-2">
                    <span className="block font-medium">
                      {send.first_name || send.email}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {send.email}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        send.status === "failed" && "text-destructive",
                      )}
                      title={send.error ?? undefined}
                    >
                      {SEND_STATUS[send.status]}
                    </span>
                    {send.unsubscribed_at ? (
                      <span className="block text-xs text-destructive">
                        Se dio de baja
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {send.opened_at
                      ? `${formatDateTime(send.opened_at)}${send.open_count > 1 ? ` · ${send.open_count} veces` : ""}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {send.click_count ? (
                      <strong>{send.click_count}</strong>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {send.client_id ? (
                      <Link
                        to={`/clientes?cliente=${send.client_id}`}
                        className="text-xs font-medium hover:underline"
                      >
                        Ficha
                      </Link>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!sortedRows.length ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    Nadie en esta lista todavía.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Las aperturas son orientativas: Apple Mail las marca siempre y algunos
          correos bloquean las imágenes. Los clics sí son fiables.
        </p>
      </div>
    </div>
  );
};
